import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Target, TrendingUp, BarChart3, Zap, Users, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import SEOHead from '../components/SEOHead';
import { scrollToSection } from '../utils/smoothScroll';
import useSectionProgress from '../hooks/useSectionProgress';

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ease-out ${className} ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const MetodoPage = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const methodFillRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const methodRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  const steps = [
    { 
      letter: 'D', 
      title: 'Diagnosi', 
      desc: 'Analisi profonda del tuo business, mercato e competitor per identificare le vere leve di crescita.',
      icon: Target,
      details: [
        'Audit completo del posizionamento attuale',
        'Analisi competitiva approfondita',
        'Mappatura dei punti di forza e debolezza',
        'Identificazione delle opportunità nascoste'
      ]
    },
    { 
      letter: 'O', 
      title: 'Obiettivi', 
      desc: 'Definizione di KPI chiari e misurabili. Saprai esattamente dove stai andando e come misurare il successo.',
      icon: Lightbulb,
      details: [
        'Obiettivi SMART personalizzati',
        'KPI di business e marketing allineati',
        'Timeline realistiche e ambiziose',
        'Milestone intermedie per tracciare i progressi'
      ]
    },
    { 
      letter: 'M', 
      title: 'Mappa', 
      desc: 'Strategia dettagliata con roadmap trimestrale. Ogni azione è pianificata e prioritizzata.',
      icon: BarChart3,
      details: [
        'Piano strategico a 12 mesi',
        'Roadmap trimestrale dettagliata',
        'Prioritizzazione delle iniziative',
        'Budget e risorse allocate'
      ]
    },
    { 
      letter: 'I', 
      title: 'Implementazione', 
      desc: 'Esecuzione guidata con il mio supporto diretto. Non ti lascio solo con un PDF.',
      icon: Users,
      details: [
        'Affiancamento operativo settimanale',
        'Review e feedback continui',
        'Supporto nelle decisioni critiche',
        'Formazione del team interno'
      ]
    },
    { 
      letter: 'N', 
      title: 'Numeri', 
      desc: 'Monitoraggio costante delle metriche. Decisioni basate su dati, non su sensazioni.',
      icon: TrendingUp,
      details: [
        'Dashboard personalizzata',
        'Report mensili dettagliati',
        'Analisi delle performance',
        'Ottimizzazione basata sui dati'
      ]
    },
    { 
      letter: 'A', 
      title: 'Accelerazione', 
      desc: 'Ottimizzazione continua e scaling delle strategie vincenti. Crescita esponenziale.',
      icon: Zap,
      details: [
        'Identificazione dei canali più performanti',
        'Scaling delle campagne vincenti',
        'Test continui per migliorare',
        'Espansione verso nuovi mercati'
      ]
    },
  ];

  const activeMethod = useSectionProgress(methodRef, methodFillRef, steps.length);

  const comparisons = [
    {
      type: 'bad',
      title: 'Senza metodo',
      color: 'bg-red-50',
      iconBg: 'bg-red-100',
      icon: '✕',
      items: [
        { text: 'Azioni casuali e disconnesse', icon: Target },
        { text: 'Budget sprecato in test inutili', icon: BarChart3 },
        { text: 'Risultati imprevedibili', icon: TrendingUp },
        { text: 'Frustrazione continua', icon: Users }
      ]
    },
    {
      type: 'medium',
      title: 'Metodo generico',
      color: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      icon: '~',
      items: [
        { text: 'Template standardizzati', icon: Target },
        { text: 'Non adattato al tuo business', icon: Lightbulb },
        { text: 'Risultati mediocri', icon: TrendingUp },
        { text: 'Nessun supporto reale', icon: Users }
      ]
    },
    {
      type: 'good',
      title: 'Metodo D.O.M.I.N.A.',
      color: 'bg-[#0b114e]',
      iconBg: 'bg-[#d4883a]',
      icon: '✓',
      textColor: 'text-white',
      items: [
        { text: 'Personalizzato per te', icon: Target },
        { text: 'Ogni euro investito tracciato', icon: BarChart3 },
        { text: 'Risultati misurabili', icon: TrendingUp },
        { text: 'Supporto diretto continuo', icon: Users }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEOHead page="metodo" />
      <Navbar />
      
      {/* Hero Section - Full Screen like Chi Sono */}
      <section className="relative min-h-screen bg-[#0b114e] overflow-hidden">
        {/* Background Image - Extended on mobile to avoid edge line */}
        <div 
          className="absolute top-0 right-0 h-full w-full lg:w-[55%]"
          style={isMobile ? { left: '-20%', right: 'auto', width: '120%' } : {}}
        >
          <div 
            className="absolute inset-0 bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url('/media/nicola-hero.webp')`,
              backgroundPosition: isMobile ? '-30% top' : 'center top',
            }}
          />
          <div className="absolute inset-0 bg-[#0b114e]/5" />
          {/* Gradient inside image container */}
          <div className={`absolute inset-0 ${
            isMobile 
              ? 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/80 via-40% to-transparent to-70%' 
              : 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/70 via-40% to-transparent to-80%'
          }`} />
        </div>

        {/* Content */}
        <div className="relative min-h-screen flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="max-w-2xl">
              {/* Badge */}
              <div 
                className={`hidden sm:inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: '200ms' }}
              >
                <span className="w-2 h-2 bg-[#d4883a] rounded-full animate-pulse" />
                <span className="text-white/70 text-sm tracking-widest">Il mio metodo</span>
              </div>

              {/* Main Heading */}
              <h1 
                className={`text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '400ms' }}
              >
                Il Framework{' '}
                <span className="text-[#d4883a] relative">
                  D.O.M.I.N.A.
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path 
                      d="M0 4C50 4 50 4 100 4C150 4 150 4 200 4" 
                      stroke="#d4883a" 
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={`transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      style={{ 
                        strokeDasharray: 200, 
                        strokeDashoffset: isLoaded ? 0 : 200,
                        transitionDelay: '1200ms'
                      }}
                    />
                  </svg>
                </span>
              </h1>

              {/* Subtitle */}
              <p 
                className={`text-white/60 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 leading-relaxed max-w-xl transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '600ms' }}
              >
                Un metodo proprietario sviluppato in 14 anni di esperienza sul campo, 
                testato con <strong className="text-white">aziende selezionate</strong> che vogliono risultati concreti.
              </p>
              
              {/* Check indicators - compact row on mobile, spaced row on desktop */}
              <div 
                className={`flex items-center gap-2 mb-6 sm:hidden text-white/50 text-xs transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '700ms' }}
              >
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>6 fasi</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>100% personalizzato</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Misurabili</span>
              </div>
              <div 
                className={`hidden sm:flex sm:flex-row sm:flex-wrap sm:gap-6 mb-8 sm:mb-10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '700ms' }}
              >
                {[
                  { label: '6 fasi strutturate', icon: CheckCircle },
                  { label: 'Personalizzato al 100%', icon: CheckCircle },
                  { label: 'Risultati misurabili', icon: CheckCircle },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 text-white/60 text-sm sm:text-base transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${800 + index * 100}ms` }}
                  >
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4883a]" />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div 
                className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '1000ms' }}
              >
                <button
                  onClick={scrollToContact}
                  data-testid="metodo-cta-button"
                  className="group bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all flex items-center justify-center gap-2 text-base sm:text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
                >
                  Scopri come applicarlo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div 
          className={`absolute bottom-8 left-6 lg:left-12 max-w-lg hidden sm:block transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '1200ms' }}
        >
          <div className="border-l-2 border-[#d4883a]/50 pl-4">
            <p className="text-white/40 italic text-sm">
              "Un metodo senza esecuzione è solo teoria.<br />
              Un'esecuzione senza metodo è solo caos."
            </p>
          </div>
        </div>
      </section>

      {/* Why Method Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Perché un metodo</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              La differenza tra <span className="text-[#d4883a]">sperare</span> e <span className="text-[#d4883a]">dominare</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              La maggior parte delle aziende spreca budget in tattiche random. 
              Il metodo D.O.M.I.N.A. ti guida passo dopo passo verso risultati concreti.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {comparisons.map((comp, index) => (
              <AnimatedSection key={index} delay={index * 150}>
                <div className={`${comp.color} rounded-2xl p-8 h-full transition-all duration-300 hover:shadow-lg ${comp.type === 'good' ? 'ring-2 ring-[#d4883a]/20' : ''}`}>
                  <div className={`w-14 h-14 ${comp.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <span className={`text-2xl font-bold ${comp.type === 'good' ? 'text-white' : comp.type === 'bad' ? 'text-red-500' : 'text-amber-500'}`}>
                      {comp.icon}
                    </span>
                  </div>
                  <h3 className={`text-xl font-bold mb-4 ${comp.textColor || 'text-[#0b114e]'}`} style={{ fontFamily: 'Georgia, serif' }}>
                    {comp.title}
                  </h3>
                  <ul className={`space-y-3 ${comp.textColor ? 'text-white/80' : 'text-gray-600'}`}>
                    {comp.items.map((item, i) => {
                      const ItemIcon = item.icon;
                      return (
                        <li key={i} className="flex items-center gap-3">
                          <ItemIcon className={`w-4 h-4 flex-shrink-0 ${comp.type === 'good' ? 'text-[#d4883a]' : comp.type === 'bad' ? 'text-red-400' : 'text-amber-500'}`} />
                          {item.text}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* D.O.M.I.N.A. Steps Section */}
      <section className="py-20 lg:py-32 bg-[#f8f9fa] relative overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#d4883a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#0b114e]/5 rounded-full blur-3xl" />
        
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <AnimatedSection className="text-center mb-16 lg:mb-24">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Le 6 fasi</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              I passi verso il <span className="text-[#d4883a]">successo</span>
            </h2>
          </AnimatedSection>
          
          {/* Method Steps with Progress Line */}
          <div className="relative" ref={methodRef}>
            {/* Progress line - background */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-[#0b114e]/10 -translate-x-1/2 rounded-full" />
            
            {/* Progress line - filled */}
            <div 
              ref={methodFillRef}
              className="hidden lg:block absolute left-1/2 top-0 h-full w-1 bg-[#d4883a] rounded-full origin-top will-change-transform"
              style={{ 
                transform: 'translateX(-50%) scaleY(0)',
                boxShadow: '0 0 10px rgba(212, 136, 58, 0.5)'
              }}
            />
            
            <div className="space-y-12 lg:space-y-0">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index <= activeMethod;
                
                return (
                  <AnimatedSection 
                    key={index}
                    delay={index * 100}
                    className={`relative lg:flex lg:items-center lg:min-h-[280px] ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                  >
                    {/* Content */}
                    <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16 lg:text-left'}`}>
                      <div 
                        className={`bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 ${index % 2 === 0 ? 'lg:mr-8' : 'lg:ml-8'} ${isActive ? 'ring-2 ring-[#d4883a]/20' : ''}`}
                      >
                        <div className={`flex items-center gap-4 mb-4 ${index % 2 === 0 ? 'lg:flex-row-reverse' : ''}`}>
                          <span className="text-5xl font-bold text-[#d4883a]/20">{step.letter}</span>
                          <div className={index % 2 === 0 ? 'lg:text-right' : ''}>
                            <h3 
                              className="text-2xl font-bold text-[#0b114e]"
                              style={{ fontFamily: 'Georgia, serif' }}
                            >
                              {step.title}
                            </h3>
                            <p className="text-[#d4883a] text-sm font-medium">Fase {index + 1} di 6</p>
                          </div>
                        </div>
                        <p className="text-gray-600 mb-4 leading-relaxed">{step.desc}</p>
                        <ul className={`space-y-2 ${index % 2 === 0 ? 'lg:text-right' : ''}`}>
                          {step.details.map((detail, i) => (
                            <li key={i} className={`flex items-center gap-2 text-gray-500 text-sm ${index % 2 === 0 ? 'lg:flex-row-reverse' : ''}`}>
                              <CheckCircle className="w-4 h-4 text-[#d4883a] flex-shrink-0" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    {/* Timeline dot with icon */}
                    <div 
                      className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-20 h-20 rounded-full items-center justify-center shadow-xl z-10 transition-all duration-500 ${
                        isActive 
                          ? 'bg-[#d4883a] scale-110' 
                          : 'bg-[#0b114e]'
                      }`}
                      style={isActive ? { boxShadow: '0 0 20px rgba(212, 136, 58, 0.5)' } : {}}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Empty space for the other side */}
                    <div className="hidden lg:block lg:w-1/2" />
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 lg:py-32 bg-[#0b114e] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4883a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">I risultati</p>
            <h2 
              className="text-3xl lg:text-5xl text-white mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Cosa puoi <span className="text-[#d4883a]">aspettarti</span>
            </h2>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { value: '14+', label: 'Anni di esperienza', desc: 'di consulenza strategica' },
              { value: '€2.5M+', label: 'Revenue generata', desc: 'per i clienti' },
              { value: '6', label: 'Fasi', desc: 'del metodo D.O.M.I.N.A.' },
              { value: '100%', label: 'Personalizzato', desc: 'adattato al tuo business' },
            ].map((stat, index) => (
              <AnimatedSection key={index} delay={index * 100} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-[#d4883a] mb-2">{stat.value}</p>
                <p className="text-white font-semibold mb-1">{stat.label}</p>
                <p className="text-white/60 text-sm">{stat.desc}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8f9fa] to-transparent" />
        <div className="absolute top-1/4 right-10 w-32 h-32 border-2 border-[#d4883a]/20 rounded-full animate-spin" style={{ animationDuration: '20s' }} />
        
        <AnimatedSection className="max-w-[800px] mx-auto px-6 lg:px-12 text-center relative">
          <h2 
            className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Pronto a <span className="text-[#d4883a]">dominare</span> il tuo mercato?
          </h2>
          <p className="text-gray-600 text-lg lg:text-xl mb-10 max-w-2xl mx-auto">
            Scopri come il metodo D.O.M.I.N.A. può trasformare il marketing della tua azienda. 
            Prenota una consulenza gratuita e analizziamo insieme il tuo caso specifico.
          </p>
          <button
            onClick={scrollToContact}
            data-testid="metodo-cta-final-button"
            className="group inline-flex items-center gap-3 bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-10 py-5 rounded-full transition-all text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
          >
            Richiedi una consulenza gratuita
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </button>
        </AnimatedSection>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default MetodoPage;
